import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, XCircle, CheckCircle2 } from "lucide-react";
import { Reveal } from "./Reveal";

const BEFORE = [
  { label: "People remember", note: "what to do — hopefully" },
  { label: "Emails", note: "details buried in threads" },
  { label: "Excel", note: "three versions of the truth" },
  { label: "Phone calls", note: "\"did anyone call them?\"" },
  { label: "Manual entry", note: "the same facts, typed again" },
  { label: "Missed information", note: "discovered too late" },
];

const AFTER = [
  { label: "The system remembers", note: "entered once, kept current" },
  { label: "Customer comes in", note: "details flow to the right people" },
  { label: "Team knows what to do", note: "each role sees its own list" },
  { label: "Work moves forward", note: "nothing waits on a phone call" },
  { label: "Invoice is created", note: "written from the work itself" },
  { label: "You see what needs attention", note: "before it becomes a problem" },
];

export default function BeforeAfterFlow() {
  const [mode, setMode] = useState("after");
  const steps = mode === "before" ? BEFORE : AFTER;

  return (
    <section id="flow" className="py-20 lg:py-32 bg-white border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-12 lg:mb-16 items-end">
          <Reveal className="col-span-12 lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-4">
              The whole idea, in two rows
            </p>
            <h2 className="font-display font-semibold tracking-tight leading-[1.12] text-brand-navy text-2xl sm:text-3xl lg:text-4xl">
              Before, people remember. After, the system does.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="col-span-12 lg:col-span-5 flex lg:justify-end">
            <div
              data-testid="before-after-toggle"
              className="inline-flex rounded-full bg-slate-100 p-1.5"
              role="tablist"
              aria-label="Before and after"
            >
              {["before", "after"].map((m) => (
                <button
                  key={m}
                  role="tab"
                  aria-selected={mode === m}
                  data-testid={`toggle-${m}`}
                  onClick={() => setMode(m)}
                  className={`rounded-full px-6 py-2.5 text-sm font-semibold capitalize transition-all duration-300 ${
                    mode === m
                      ? "bg-brand-navy text-white shadow"
                      : "text-slate-500 hover:text-brand-navy"
                  }`}
                >
                  {m === "before" ? "How it runs today" : "With Ocean Data"}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid={mode === "before" ? "before-flow-container" : "after-flow-container"}
            className={`rounded-3xl border p-6 sm:p-10 transition-colors duration-500 ${
              mode === "before"
                ? "bg-slate-50 border-slate-200"
                : "bg-brand-tealsoft border-brand-tealborder"
            }`}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={mode}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-wrap items-stretch gap-3"
              >
                {steps.map((step, i) => (
                  <div key={`${mode}-${step.label}`} className="flex items-center gap-3 flex-1 min-w-[140px]">
                    <div
                      data-testid={`flow-step-${mode}-${i}`}
                      className={`flex-1 rounded-xl border px-4 py-4 transition-colors duration-500 ${
                        mode === "before"
                          ? "bg-white border-slate-200"
                          : "bg-white border-brand-teal/30"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        {mode === "before" ? (
                          <XCircle size={14} className="text-slate-300 shrink-0" />
                        ) : (
                          <CheckCircle2 size={14} className="text-brand-green shrink-0" />
                        )}
                        <span className="font-display font-semibold text-brand-navy text-base leading-none">
                          {step.label}
                        </span>
                      </div>
                      <p className={`text-xs leading-snug ${mode === "before" ? "text-slate-400" : "text-brand-green"}`}>
                        {step.note}
                      </p>
                    </div>
                    {i < steps.length - 1 && (
                      <ArrowRight
                        size={16}
                        className={`shrink-0 hidden sm:block ${mode === "before" ? "text-slate-300" : "text-brand-green"}`}
                      />
                    )}
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>

            <p className="mt-8 text-sm text-slate-500 max-w-2xl leading-relaxed">
              {mode === "before"
                ? "Six links held together by memory — and the strongest link is still a person who's tired."
                : "The same business, the same people — the information just moves on its own."}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
